import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarDays, Clock, MapPin, Wifi, Check, Users, Info } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { Chip, SampleTag, StoryCard } from "../components/cards";
import ShareButtons from "../components/ShareButtons";
import { getEventBySlug, getActivityBySlug, getActivityStories, formatDate } from "../lib/content";

export default function EventDetail() {
  const { slug } = useParams();
  const event = getEventBySlug(slug);

  if (!event) {
    return (
      <main className="pt-40 pb-32 text-center" data-testid="event-not-found">
        <h1 className="font-serif text-4xl text-ink">Event not found</h1>
        <Link to="/events" className="mt-6 inline-block text-action font-semibold underline underline-offset-4">
          Back to all events
        </Link>
      </main>
    );
  }

  const isPast = event.status === "past";
  const activity = event.relatedActivity ? getActivityBySlug(event.relatedActivity) : null;
  const relatedStories = event.relatedActivity ? getActivityStories(event.relatedActivity) : [];

  return (
    <main>
      <SEO title={event.title} description={event.description} path={`/events/${slug}`} image={event.image} />

      <article className="pt-32 md:pt-40 pb-24" data-testid="event-detail">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link
            to="/events"
            data-testid="event-back"
            className="inline-flex items-center gap-2 text-sm font-medium text-ink-2 hover:text-ink transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> All events
          </Link>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Chip pillar={event.pillar} />
            <span
              className={`text-[0.7rem] font-semibold uppercase tracking-[0.15em] rounded-full px-3 py-1 border ${
                isPast ? "text-ink-2 border-line bg-sand-2" : "text-action border-action/30"
              }`}
            >
              {isPast ? "Past event" : "Upcoming"}
            </span>
            {event.isSample && <SampleTag />}
          </div>

          <h1 className="mt-5 font-serif tracking-tighter leading-[0.98] text-ink text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
            {event.title}
          </h1>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8">
              <Reveal>
                <div className="overflow-hidden rounded-xl aspect-[16/9]">
                  <img src={event.image} alt={event.imageAlt} className="h-full w-full object-cover" />
                </div>
              </Reveal>

              <p className="mt-10 text-base md:text-lg leading-relaxed text-ink-2 max-w-2xl">
                {event.description}
              </p>

              {isPast && event.recapNote && (
                <div className="mt-8 border border-line rounded-xl bg-sand-2 p-6 max-w-2xl" data-testid="event-recap-note">
                  <p className="text-xs uppercase tracking-[0.2em] font-semibold text-ink-2">Recap</p>
                  <p className="mt-3 text-sm leading-relaxed text-ink-2">{event.recapNote}</p>
                </div>
              )}

              {event.whatToExpect.length > 0 && (
                <section className="mt-12 max-w-2xl" data-testid="event-what-to-expect">
                  <h2 className="font-serif text-3xl tracking-tight text-ink">What to expect</h2>
                  <ul className="mt-6 space-y-4">
                    {event.whatToExpect.map((item) => (
                      <li key={item} className="flex gap-3 text-sm md:text-base text-ink-2 leading-relaxed">
                        <Check className="h-5 w-5 shrink-0 text-action" /> {item}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {event.whoCanJoin && (
                <section className="mt-12 max-w-2xl" data-testid="event-who-can-join">
                  <h2 className="font-serif text-3xl tracking-tight text-ink flex items-center gap-3">
                    <Users className="h-6 w-6 text-action" /> Who can participate
                  </h2>
                  <p className="mt-4 text-sm md:text-base leading-relaxed text-ink-2">{event.whoCanJoin}</p>
                </section>
              )}

              {event.howToParticipate && (
                <section className="mt-12 max-w-2xl" data-testid="event-how-to">
                  <h2 className="font-serif text-3xl tracking-tight text-ink">How to take part</h2>
                  <p className="mt-4 text-sm md:text-base leading-relaxed text-ink-2">{event.howToParticipate}</p>
                </section>
              )}

              {event.organiser && (
                <p className="mt-12 flex items-start gap-3 text-sm text-ink-2 max-w-2xl">
                  <Info className="h-4 w-4 mt-0.5 shrink-0" />
                  Organised by {event.organiser}.
                </p>
              )}

              {activity && (
                <Link
                  to={`/activities/${activity.slug}`}
                  data-testid="event-related-activity"
                  className="group mt-12 flex items-center justify-between gap-6 border border-line rounded-xl p-6 md:p-8 bg-sand-2 max-w-2xl transition-colors hover:bg-sand-3"
                >
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-ink-2">Part of the activity</p>
                    <p className="mt-2 font-serif text-2xl tracking-tight text-ink">{activity.title}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 shrink-0 text-action transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              )}

              {relatedStories.length > 0 && (
                <section className="mt-16" data-testid="event-related-stories">
                  <h2 className="font-serif text-3xl tracking-tight text-ink">Related stories</h2>
                  <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-10">
                    {relatedStories.map((story) => (
                      <StoryCard key={story.slug} story={story} withImage />
                    ))}
                  </div>
                </section>
              )}
            </div>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 border border-line rounded-xl bg-sand-2 p-7 md:p-8" data-testid="event-info-card">
                <h2 className="text-xs uppercase tracking-[0.25em] font-semibold text-ink-2">Details</h2>
                <dl className="mt-6 space-y-5 text-sm">
                  <div className="flex gap-3">
                    <CalendarDays className="h-5 w-5 shrink-0 text-action" />
                    <div>
                      <dt className="text-ink-2">Date</dt>
                      <dd className="font-semibold text-ink">
                        {formatDate(event.startDate, { weekday: "long" })}
                        {event.endDate && ` — ${formatDate(event.endDate, { weekday: "long" })}`}
                      </dd>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Clock className="h-5 w-5 shrink-0 text-action" />
                    <div>
                      <dt className="text-ink-2">Time</dt>
                      <dd className="font-semibold text-ink">{event.startTime}</dd>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    {event.isOnline ? (
                      <Wifi className="h-5 w-5 shrink-0 text-action" />
                    ) : (
                      <MapPin className="h-5 w-5 shrink-0 text-action" />
                    )}
                    <div>
                      <dt className="text-ink-2">{event.isOnline ? "Format" : "Location"}</dt>
                      <dd className="font-semibold text-ink">
                        {event.isOnline ? "Online" : event.location}
                      </dd>
                    </div>
                  </div>
                </dl>

                {!isPast && (
                  event.registrationUrl ? (
                    <a
                      href={event.registrationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="event-register-cta"
                      className="mt-8 block text-center rounded-full bg-action text-sand px-6 py-3.5 text-sm font-semibold transition-transform duration-300 hover:scale-[1.03] active:scale-95"
                    >
                      {event.ctaLabel}
                    </a>
                  ) : (
                    <Link
                      to="/get-involved"
                      data-testid="event-register-cta"
                      className="mt-8 block text-center rounded-full bg-action text-sand px-6 py-3.5 text-sm font-semibold transition-transform duration-300 hover:scale-[1.03] active:scale-95"
                    >
                      Register interest
                    </Link>
                  )
                )}

                <div className="mt-8 pt-6 border-t border-line">
                  <ShareButtons title={event.title} />
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>
    </main>
  );
}
